import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import { Sparkles, ArrowLeft, Brain, Shield, Clock, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function AIInsights() {
  const { isDarkMode } = useTheme();
  const { vulnerabilityId } = useParams();
  const navigate = useNavigate();
  const [vulnerability, setVulnerability] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchVulnerability();
  }, [vulnerabilityId]);

  const fetchVulnerability = async () => {
    try {
      const response = await api.get(`/vulnerabilities/${vulnerabilityId}`);
      setVulnerability(response.data);
      setInsights(response.data.ai_insights);
    } catch (error) {
      toast.error('Failed to fetch vulnerability');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInsights = async () => {
    setGenerating(true);
    try {
      const response = await api.post(`/vulnerabilities/${vulnerabilityId}/ai-insights`);
      setInsights(response.data.insights);
      toast.success('AI insights generated successfully');
    } catch (error) {
      toast.error('Failed to generate AI insights');
    } finally {
      setGenerating(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/30',
      high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      low: 'bg-green-500/20 text-green-400 border-green-500/30',
    };
    return colors[severity] || colors.medium;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-indigo-400 font-rajdhani text-lg">Loading insights...</div>
      </div>
    );
  }

  if (!vulnerability) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <AlertTriangle className="w-16 h-16 text-zinc-600 mb-4" />
        <p className="text-zinc-400 font-inter text-sm">Vulnerability not found</p>
        <Button onClick={() => navigate('/vulnerability-hub')} className="mt-4">
          Back to Vulnerability Hub
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="ai-insights-container" className="space-y-5">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/vulnerability-hub')}
          className={isDarkMode ? 'text-zinc-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className={`w-12 h-12 bg-violet-600/20 border rounded-xl flex items-center justify-center ${isDarkMode ? 'border-violet-500/30' : 'border-violet-300'}`}>
            <Brain className="w-6 h-6 text-violet-400" />
          </div>
          <div>
            <h1 className={`font-rajdhani font-bold text-3xl ${isDarkMode ? 'text-white text-shadow-neon' : 'text-slate-900'}`}>
              AI Security Insights
            </h1>
            <p className={`font-inter text-sm mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
              Advanced AI-Powered Analysis
            </p>
          </div>
        </div>
      </div>

      {/* Vulnerability Overview */}
      <div className={`rounded-2xl p-5 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <span className={`px-3 py-1 rounded-full text-xs font-mono uppercase border ${getSeverityColor(vulnerability.severity)}`}>
                {vulnerability.severity}
              </span>
              {vulnerability.cve_id && (
                <span className={`text-xs font-mono ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{vulnerability.cve_id}</span>
              )}
              {vulnerability.cvss_score && (
                <span className={`text-xs font-mono ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>CVSS: {vulnerability.cvss_score}</span>
              )}
            </div>
            
            <h2 className={`font-rajdhani font-semibold text-2xl mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {vulnerability.title}
            </h2>
            
            <p className={`font-inter text-sm mb-4 ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>
              {vulnerability.description}
            </p>

            <div className={`flex items-center gap-4 text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
              <div className="flex items-center gap-2">
                <Clock className="w-3 h-3" />
                <span>Discovered: {new Date(vulnerability.discovered_at).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-3 h-3" />
                <span>Status: {vulnerability.status}</span>
              </div>
            </div>
          </div>

          {!insights && (
            <Button
              data-testid="generate-insights-button"
              onClick={handleGenerateInsights}
              disabled={generating}
              className="bg-violet-600 hover:bg-violet-500 font-rajdhani shadow-neon-indigo"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {generating ? 'Generating...' : 'Generate AI Insights'}
            </Button>
          )}
        </div>
      </div>

      {/* AI Insights Section */}
      {generating && (
        <div className={`rounded-2xl p-8 text-center ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <div className="inline-block w-16 h-16 border-4 border-violet-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className={`font-rajdhani text-lg ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`}>Analyzing vulnerability with AI...</p>
          <p className={`text-sm mt-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>This may take a few moments</p>
        </div>
      )}

      {insights && !generating && (
        <div className="space-y-4">
          <div className={`rounded-2xl p-6 border-l-4 border-violet-500 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 bg-violet-600/20 border rounded-lg flex items-center justify-center ${isDarkMode ? 'border-violet-500/30' : 'border-violet-300'}`}>
                <Sparkles className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <h3 className={`font-rajdhani font-semibold text-xl ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>AI-Generated Insights</h3>
                <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>AI-Powered Analysis</p>
              </div>
            </div>

            <div className="prose prose-invert max-w-none">
              <div className={`font-inter text-sm leading-relaxed whitespace-pre-line ${isDarkMode ? 'text-zinc-200' : 'text-slate-700'}`}>
                {insights}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`rounded-xl p-5 cursor-pointer ${isDarkMode ? 'glass glass-hover' : 'bg-white border border-slate-200 shadow-sm hover:shadow-md'}`} onClick={() => navigate(`/remediation/${vulnerabilityId}`)}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-9 h-9 bg-emerald-600/20 border rounded-lg flex items-center justify-center ${isDarkMode ? 'border-emerald-500/30' : 'border-emerald-300'}`}>
                  <Shield className="w-4 h-4 text-emerald-400" />
                </div>
                <h4 className={`font-rajdhani font-semibold text-base ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>View Remediation Guidance</h4>
              </div>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Step-by-step instructions to fix this vulnerability</p>
            </div>

            <div className={`rounded-xl p-5 ${isDarkMode ? 'glass glass-hover' : 'bg-white border border-slate-200 shadow-sm'}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-9 h-9 bg-indigo-600/20 border rounded-lg flex items-center justify-center ${isDarkMode ? 'border-indigo-500/30' : 'border-indigo-300'}`}>
                  <Clock className="w-4 h-4 text-indigo-400" />
                </div>
                <h4 className={`font-rajdhani font-semibold text-base ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Estimated Fix Time</h4>
              </div>
              <p className={`font-rajdhani text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>2-4 hours</p>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Based on complexity analysis</p>
            </div>
          </div>
        </div>
      )}

      {!insights && !generating && (
        <div className={`rounded-2xl p-12 text-center ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <Brain className={`w-20 h-20 mx-auto mb-4 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
          <h3 className={`font-rajdhani font-semibold text-xl mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>No AI Insights Yet</h3>
          <p className={`font-inter text-sm mb-6 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
            Generate AI-powered security insights and remediation recommendations
          </p>
          <Button
            onClick={handleGenerateInsights}
            className="bg-violet-600 hover:bg-violet-500 font-rajdhani shadow-neon-indigo"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Generate Insights Now
          </Button>
        </div>
      )}
    </div>
  );
}
